import { useRef, useState } from "react";
import { Upload, Link2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const inputCls = "h-11 w-full border border-black/20 bg-white px-3 text-sm outline-none focus:border-gavra";
const labelCls = "font-mono-hw text-[11px] uppercase tracking-widest text-black/50";

/**
 * Image field with direct upload (stored in MongoDB) + manual URL entry.
 * Emits an absolute URL string via onChange (existing external URLs keep working).
 */
export default function ImageUploadField({ label, value, onChange, testid, compact }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const absolute = `${BACKEND_URL}${data.url}`;
      onChange(absolute);
      toast.success("Image uploaded", { description: `${Math.round((data.size || 0) / 1024)} KB` });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div>
      {label && <label className={labelCls}>{label}</label>}
      <div className={`mt-1.5 flex ${compact ? "items-center" : "items-start"} gap-3`}>
        <div className={`${compact ? "h-11 w-11" : "h-16 w-16"} shrink-0 overflow-hidden border border-black/10 bg-secondary`}>
          {value ? (
            <img src={value} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-black/20"><Link2 className="h-4 w-4" /></div>
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <input
              data-testid={testid ? `${testid}-url` : undefined}
              value={value || ""}
              onChange={(e) => onChange(e.target.value)}
              placeholder="Paste image URL or upload →"
              className={`${inputCls} h-10`}
            />
            {value && (
              <button type="button" onClick={() => onChange("")} title="Clear" className="border border-black/20 px-2.5 py-2 hover:border-destructive hover:text-destructive">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={testid ? `${testid}-file` : undefined} />
          <button
            type="button"
            data-testid={testid ? `${testid}-upload-btn` : undefined}
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="inline-flex items-center gap-2 border border-black px-4 py-2 text-[11px] font-bold uppercase tracking-[0.15em] text-black transition-colors hover:bg-black hover:text-white disabled:opacity-50"
          >
            {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            {uploading ? "Uploading…" : "Upload image"}
          </button>
        </div>
      </div>
    </div>
  );
}
